<template>
	<div>
		<div ref="map_element" class="map"></div>
		<div ref="popup" class="ol-popup text-center">
			<a id="popup-closer" class="ol-popup-closer" @click="closePopup"></a>
			<slot name="popup" v-bind:item="popup_data">
				<div class="text-center" id="popup-content"></div>
			</slot>
		</div>
		<button v-if="props.downloadButton" id="export-png-button" @click="exportMapAsPNG">
			<i class="bi bi-download icon"></i>
			<span class="d-none d-sm-inline"> Scarica mappa</span>
		</button>
		<button id="toggle-layer-button" class="change-layer-button" data-bs-toggle="dropdown" aria-expanded="false">
			<i class="bi bi-layers"></i>
		</button>
		<ul class="dropdown-menu">
			<li>
				<a class="dropdown-item" @click="setLayer('osm')"
					:class="{ active: data.currentLayer === 'osm' }">Geografica</a>
			</li>
			<li>
				<a class="dropdown-item" @click="setLayer('orto')"
					:class="{ active: data.currentLayer === 'orto' }">OrtoFoto</a>
			</li>
		</ul>
	</div>
</template>

<script setup>
import { ref, onMounted, watchEffect, reactive } from 'vue'
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import TileWMS from 'ol/source/TileWMS.js';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import Overlay from 'ol/Overlay.js';
import ImageLayer from 'ol/layer/Image.js';
import { transformExtent } from 'ol/proj';
import { fromLonLat, transform } from 'ol/proj'
import { osm, orto } from "@/utils/pcr.layers.js";
import { getMarker } from './MapMarkers.js'

const props = defineProps({
	downloadButton: { type: Boolean, default: false },
	markers: { type: Array, default: () => [] },
	currentSensor: { type: String, default: "Temperatura" },
	center: { type: Array, default: () => [13.003024992385106, 46.08506324410948] },
	zoom: { type: Number, default: 9 },
	data: { type: [Object, Array], default: () => [] },
	current: { type: Number, default: 0 },
	current_interval: { type: String, default: 'SRT-1' }
});

const emit = defineEmits(["mapclick"]);
const map_element = ref(null);
let map = ref(null);
let closePopup = null;
let popup_data = ref({});
let popup = ref(null);
let layers = [osm]

const data = reactive({
	currentLayer: 'osm', // Default layer
});

const setLayer = (layer) => {
	data.currentLayer = layer;
	updateMapLayers();
};

const updateMapLayers = () => {
	layers = data.currentLayer === 'osm' ? [osm] : [orto];
	map.value.setLayers(buildMapLayers());
};

const buildMapLayers = () => {
	return layers.map(l => {
		return new TileLayer({
			zIndex: l.zIndex,
			code: l.code,
			opacity: l.opacity,
			source: new TileWMS({
				url: l.url,
				params: l.layer_options,
				attributions: l.attribution,
				crossOrigin: ''
			})
		});
	})
}

const buildLayers = () => {

	let res = []

	if (props.data.length !== 0) {
		res = res.concat(props.data)
	} else {
		if (!props.markers || props.markers.length == 0)
			return res;

		let markers = props.markers.map(m => new Feature({
			geometry: new Point(fromLonLat([m.lon, m.lat])),
			...m
		}));

		let markerLayer = new VectorLayer({
			source: new VectorSource({
				features: markers
			}),
			style: (f) => {
				let ss = getMarker(f.get('ui'));
				if (Array.isArray(ss)) {
					let t = ss.map(s => s.getText()).filter(t => t != null)[0];
					if (t)
						t.setText(f.get('ui_label'));
				}
				return ss;
			}
		});

		res = res.concat([markerLayer]);
	}

	return res;
};

const combineLayers = () => {
	const mapLayer = buildMapLayers();
	const markerAndImagesLayers = buildLayers();
	const combinedLayers = [...mapLayer, ...markerAndImagesLayers]
	return combinedLayers;
}

const buildMap = () => {

	const popupOverlay = new Overlay({
		element: popup.value,
		autoPan: true,
		autoPanAnimation: {
			duration: 250,
		},
	});

	closePopup = () => {
		popupOverlay.setPosition(undefined);
		return false;
	};

	const opts = {
		layers: combineLayers(),
		overlays: [popupOverlay],
		view: new View({
			center: fromLonLat(props.center),
			zoom: props.zoom,
			minZoom: 7,
			maxZoom: 20,
		}),
		// target: "map",
		target: map_element.value,
	};
	let m = new Map(opts);

	m.on('click', (evt) => {
		const feature = map.value.forEachFeatureAtPixel(
			evt.pixel,
			(feature) => feature
		);

		if (feature) {
			const coordinates = feature.getGeometry().getCoordinates();
			emit('markerClick', { feature });
			popup_data.value = feature;
			popupOverlay.setPosition(coordinates);
		}
		else {
			let lonlat = transform(evt.coordinate, 'EPSG:3857', 'EPSG:4326');
			emit('mapclick', {
				event: evt,
				latlon: { lat: lonlat[1], lon: lonlat[0] }
			})
		}

	});

	const fireViewChanged = () => {
		let extent = m.getView().calculateExtent(m.getSize());
		let size = m.getSize();

		extent = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');

		const info = {
			bounds: extent,
			size: size
		};
		emit('view_changed', info);
	}

	m.on('moveend', fireViewChanged);
	m.on('resize', fireViewChanged);

	m.on('pointermove', (evt) => {
		const pixel = map.value.getEventPixel(evt.originalEvent);
		const hit = map.value.hasFeatureAtPixel(pixel);
		map.value.getTargetElement().style.cursor = hit ? 'pointer' : 'grab';
	});

	map.value = m;
};

const exportMapAsPNG = () => {
	map.value.once('rendercomplete', () => {
		const mapCanvas = document.createElement('canvas');
		const size = map.value.getSize();
		mapCanvas.width = size[0];
		mapCanvas.height = size[1];
		const mapContext = mapCanvas.getContext('2d');

		mapContext.fillStyle = '#ffffff';
		mapContext.fillRect(0, 0, size[0], size[1]);

		Array.prototype.forEach.call(
			map.value.getViewport().querySelectorAll('.ol-layer canvas, canvas.ol-layer'),
			(canvas) => {
				if (canvas.width > 0) {
					const opacity = canvas.parentNode.style.opacity || canvas.style.opacity;
					mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
					let matrix;
					const transform = canvas.style.transform;
					if (transform) {
						matrix = transform.match(/^matrix\(([^\(]*)\)$/)[1].split(',').map(Number);
					} else {
						matrix = [parseFloat(canvas.style.width) / canvas.width, 0, 0, parseFloat(canvas.style.height) / canvas.height, 0, 0];
					}

					CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);

					const backgroundColor = canvas.parentNode.style.backgroundColor;
					if (backgroundColor) {
						mapContext.fillStyle = backgroundColor;
						mapContext.fillRect(0, 0, canvas.width, canvas.height);
					}

					mapContext.drawImage(canvas, 0, 0);
				}
			}
		);

		mapContext.globalAlpha = 1;
		mapContext.setTransform(1, 0, 0, 1, 0, 0);

		const logos = [
			{ src: '/map_icons/logo.png', positionX: mapCanvas.width - 110, positionY: mapCanvas.height - 110, width: 50, height: 25 },
			{ src: `/map_icons/${props.currentSensor === "temperature" ? "temperatura" : props.currentSensor === "precipitations_1h" ? "piogge1h" : props.currentSensor === "precipitations_24h" ? "piogge24h" : props.currentSensor === "humidity" ? "umidita" : props.currentSensor === "pressure" ? "pressione" : props.currentSensor === "wind" ? "vento" : "none"}.png`, positionX: `${props.currentSensor === "wind" ? mapCanvas.width - 160 : props.currentSensor === "pressure" ? mapCanvas.width - 100 : mapCanvas.width - 80}`, positionY: 10, width: 80, height: 40 },
		];

		logos.forEach((logo) => {
			const logoImage = new Image();
			logoImage.src = logo.src;

			logoImage.onload = () => {
				mapContext.drawImage(logoImage, logo.positionX, logo.positionY);

				if (logos.indexOf(logo) === logos.length - 1) {
					const link = document.createElement('a');
					link.href = mapCanvas.toDataURL();
					link.download = 'map.png';
					link.click();
				}
			};
		});
	});

	map.value.renderSync();
};

onMounted(() => {
	watchEffect(() => {
		if (map.value == null) {
			buildMap();
		}
		else {
			let m = map.value;
			let existingLayers = m.getLayers();
			let newLayers = buildLayers();

			existingLayers.getArray().forEach(layer => {
				if (layer instanceof VectorLayer) {
					existingLayers.remove(layer);
				}
			});

			// Solo per pagina di piogge cumulate
			if (existingLayers.getArray().length === 11 && props.current_interval === "SRT_1" || props.current_interval === "SRT_3" || props.current_interval === "SRT_6") {
				let imageLayersToRemove = existingLayers.getArray().filter(layer => layer instanceof ImageLayer);
				imageLayersToRemove.forEach(layer => existingLayers.remove(layer));
			}
			//
			let imageLayersToRemove = existingLayers.getArray().filter(layer => layer instanceof ImageLayer);
			imageLayersToRemove.forEach(layer => existingLayers.remove(layer));

			
			newLayers.forEach(layer => {
				if (existingLayers.getArray().length < 11) {
					existingLayers.push(layer);
				}
			});

			let imageLayers = existingLayers.getArray().filter(layer => layer instanceof ImageLayer);
			imageLayers.forEach((layer, index) => layer.setOpacity(index === props.current ? 1 : 0));
		}
	})
});
</script>

<style lang="scss">
@import "ol/ol.css";

.change-layer-button {
	font-variant: small-caps;
	font-weight: bold;
	font-size: 1em;
	border: 1px solid rgba(124, 124, 124, 0.2);
	background-color: rgb(255, 255, 255);
	border-radius: 5px;
	color: #0067b1;
	padding: 5px 10px;
	position: absolute;
	top: 100px;
	right: 10px;
	z-index: 1000;

	&:hover {
		background-color: #ccc;
	}

	.icon {
		color: #a3a3a3;
	}
}

.dropdown-item {
	color: #0067b1;
	font-variant: small-caps;
	font-weight: bold;
	background-color: #fff;
	opacity: 0.8;

	&:hover {
		color: #0067b1;
		background-color: #ccc;
		cursor: pointer;
	}

	&.active {
		background-color: #0067b1;
		color: #fff;
	}
}

#export-png-button {
	font-variant: small-caps;
	font-weight: bold;
	font-size: 1em;
	border: 1px solid rgba(124, 124, 124, 0.2);
	background-color: rgb(255, 255, 255);
	border-radius: 5px;
	color: #0067b1;
	padding: 5px 10px;
	position: absolute;
	bottom: 10px;
	right: 10px;
	z-index: 1000;

	&:hover {
		background-color: #ccc;
	}

	.icon {
		color: #a3a3a3;
	}
}

.map .ol-zoom {
	top: 10px;
	left: auto;
	right: 10px;
}

.ol-control button {
	font-size: 1.5em !important;

	&:hover {
		outline: none;
		color: #0067b1;
	}
}

button.ol-zoom-out,
button.ol-zoom-in {
	border: 1px solid rgba(124, 124, 124, 0.2);
	color: #0067b1;

	&:hover {
		background-color: #ccc;
		outline: none;
	}

	&:focus {
		outline: none;
		color: #0067b1;
	}
}

.map {
	height: 100%;
}

.ol-popup {
	position: absolute;
	background-color: white;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
	padding: 15px;
	border-radius: 10px;
	border: 1px solid #cccccc;
	bottom: 12px;
	left: -125px;
	min-width: 250px;
}

.ol-popup:after,
.ol-popup:before {
	top: 100%;
	border: solid transparent;
	content: " ";
	height: 0;
	width: 0;
	position: absolute;
	pointer-events: none;
}

.ol-popup:after {
	border-top-color: white;
	border-width: 10px;
	left: 125px;
	margin-left: -10px;
}

.ol-popup:before {
	border-top-color: #cccccc;
	border-width: 11px;
	left: 125px;
	margin-left: -11px;
}

.ol-popup-closer {
	text-decoration: none;
	position: absolute;
	top: 2px;
	right: 8px;

	&:hover {
		cursor: pointer;
	}
}

.ol-popup-closer:after {
	content: "✖";
}

/*Per cancellare il trademark*/
div.ol-attribution.ol-unselectable.ol-control.ol-uncollapsible {
	display: none;
}

/*Per far entrare la mappa sul container nella pagina stazioni*/
div.ol-viewport.ol-touch {
	width: auto !important;
}
</style>